import { artUrl } from "./art-url";

// A set page's checklist links each card to Scryfall; hovering or focusing one shows the card beside
// it: the image the site serves, or failing that Scryfall's from the set, then its newest printing.

const width = 244;
const height = Math.round((width * 680) / 488);
const gap = 16;
const delay = 120;

export function cardPreviews(root: HTMLElement) {
  const set = (root.dataset.set ?? "").toLowerCase();
  const img = document.createElement("img");
  img.className = "card-preview";
  img.alt = "";
  img.width = width;
  img.height = height;
  img.hidden = true;
  document.body.append(img);

  let current: HTMLAnchorElement | null = null;
  let timer = 0;
  let tried = 0;
  const sources = (name: string) => [artUrl(name), scryfallImage(name, set), scryfallImage(name, "")];

  // Right of the card's name, or left of it if that runs off the screen, level with it but on screen.
  function place() {
    if (!current) return;
    const r = current.getBoundingClientRect();
    let x = r.right + gap;
    if (x + width > innerWidth - gap) x = r.left - gap - width;
    const y = Math.min(Math.max(r.top + r.height / 2 - height / 2, gap), innerHeight - gap - height);
    img.style.translate = `${Math.max(x, gap)}px ${y}px`;
  }

  function show(link: HTMLAnchorElement) {
    clearTimeout(timer);
    img.hidden = true;
    img.removeAttribute("src");
    current = link;
    timer = window.setTimeout(() => {
      tried = 0;
      img.src = sources(link.textContent ?? "")[0];
    }, delay);
  }

  function hide() {
    clearTimeout(timer);
    current = null;
    img.hidden = true;
    img.removeAttribute("src");
  }

  img.addEventListener("load", () => {
    if (!current) return;
    place();
    img.hidden = false;
  });
  img.addEventListener("error", () => {
    const next = current && sources(current.textContent ?? "")[++tried];
    if (next) img.src = next;
    else hide();
  });

  root.addEventListener("pointerover", (e) => {
    const link = (e.target as Element).closest("a");
    if (e.pointerType === "mouse" && link && link !== current) show(link);
  });
  root.addEventListener("pointerout", (e) => {
    if (current && !current.contains(e.relatedTarget as Node | null)) hide();
  });
  root.addEventListener("focusin", (e) => {
    const link = (e.target as Element).closest("a");
    if (link) show(link);
  });
  root.addEventListener("focusout", hide);
  // A focused card follows its name as the page scrolls it into view; a hovered one goes.
  addEventListener("scroll", () => {
    if (!current) return;
    if (current !== document.activeElement) hide();
    else if (!img.hidden) place();
  }, { passive: true });
  addEventListener("keydown", (e) => { if (e.key === "Escape") hide(); });
}

function scryfallImage(name: string, set: string): string {
  const inSet = set ? `&set=${encodeURIComponent(set)}` : "";
  return `https://api.scryfall.com/cards/named?exact=${encodeURIComponent(name)}${inSet}&format=image&version=normal`;
}
